/**
 * KontaktyList — Phase 4.1 list workspace.
 *
 * Source: extrapolated from the radical artboard language, calibrated against
 * Phase 2.1 (VozidlaList) and Phase 3.1 (ZakazniciList): name search, brand +
 * seller filters, and a `typ_kontaktu` (1 = Rozjednané, 2 = Ukončené,
 * 3 = Všechny) selector in the editorial DataTable + Inspector pattern.
 *
 * Filter chips (brand + seller + "podpis objednávky") read from the dedicated
 * lookup endpoints (`/v1/contacts/vehicle-brands`, `/v1/contacts/sellers`).
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useEffect, useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Inspector, FieldRow } from '@/components/Inspector';
import { LoadMoreBar } from '@/components/LoadMoreBar';
import { Badge } from '@/components/Badge';
import { Marker } from '@/components/Marker';
import { Pill } from '@/components/Pill';
import { Popover } from '@/components/Popover';
import { Field, Fields } from '@/components/Field';
import { TextInput } from '@/components/TextInput';
import { SearchInput } from '@/components/SearchInput';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Toolband } from '@/components/Toolband';
import { Menu } from '@/components/Menu';
import { userDotColor } from '@/lib/color';
import { formatDate, formatInt } from '@/lib/format';
import { linkify } from '@/lib/linkify';
import { useModuleAccess } from '@/lib/permissions';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useMeQuery } from '@/modules/dashboard/api';
import { toast } from '@/components/Toaster';
import { downloadContactsCsv } from './export';
import {
  FILTERS_STORAGE_KEY,
  readStoredFilters,
  type KontaktySearch,
} from './filters';
import {
  useContactBrandsQuery,
  useContactDetailQuery,
  useContactSellersQuery,
  useContactsInfiniteQuery,
  type Contact,
  type ContactPlan,
  type ContactType,
} from './api';
import {
  contactDisplayName,
  daysSince,
  vehicleBrandLabel,
  vehicleModelLabel,
} from './helpers';
import { MessageBody } from './MessageBody';
import { NaplanovatModal } from './NaplanovatModal';
import { OdeslatZpravuModal } from './OdeslatZpravuModal';
import { goToKontaktyTab, useKontaktyTabs, type KontaktyTab } from './tabs';

const COLUMNS_TEMPLATE = 'minmax(0,1.3fr) minmax(0,1fr) minmax(0,1.6fr) 64px 130px 168px';

/** `YYYY-MM-DD` for `offsetDays` from today (negative = past). */
function isoDate(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function KontaktyList() {
  const navigate = useNavigate();
  const tabOptions = useKontaktyTabs();
  // `?type=` is the source of truth so PlanovaneList's subtabs (and any
  // deep-link / bookmark) can land on Ukončené / Všechny instead of the
  // default Rozjednané. Default stays implicit in the URL.
  const urlSearch = useSearch({ from: '/kontakty' });
  const { isPhone } = useBreakpoint();
  const type: ContactType = urlSearch.type ?? 'rozjednane';
  const setType = (next: ContactType) => {
    navigate({
      to: '/kontakty',
      search: { ...urlSearch, type: next === 'rozjednane' ? undefined : next },
      replace: true,
    });
  };

  // Local state for the search input — immediate caret response, then a
  // debounce pushes it to the URL so the detail page's narrow rail picks it up.
  const [search, setSearch] = useState<string>(urlSearch.search ?? '');

  // Lazy-init the persisted filters once so a seller/brand selection survives a
  // round-trip to a contact detail.
  const [storedFilters] = useState(readStoredFilters);
  const [brandId, setBrandId] = useState<number | null>(storedFilters.brandId);
  const [sellerId, setSellerId] = useState<number | null>(storedFilters.sellerId);
  const [showMine, setShowMine] = useState(storedFilters.showMine);
  const [signedOnly, setSignedOnly] = useState(storedFilters.signedOnly);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify({ brandId, sellerId, showMine, signedOnly }),
      );
    } catch {
      // localStorage unavailable — in-memory filters still drive this session.
    }
  }, [brandId, sellerId, showMine, signedOnly]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [naplanovatTarget, setNaplanovatTarget] = useState<
    { contact: Contact; plan?: ContactPlan } | null
  >(null);
  const [zpravaTarget, setZpravaTarget] = useState<Contact | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Last-contact date range for the export — defaults to the past week → today,
  // as the legacy export did. Export-only — does not filter the list.
  const [exportStart, setExportStart] = useState(() => isoDate(-7));
  const [exportEnd, setExportEnd] = useState(() => isoDate(0));

  const me = useMeQuery();
  const myId = me.data?.user.id ?? null;
  const { can } = useModuleAccess();
  const canWrite = can('kontakty', 'write');

  const debouncedSearch = useDebouncedValue(search, 300);

  // Pull external URL changes (back / forward, deep-link, programmatic
  // navigation) back into the local input. Without this, the debounce-write
  // effect below would immediately overwrite the new URL with the stale
  // local input value, breaking back/forward for `?search=…` bookmarks.
  // Skip the sync while the user is mid-typing — once the debounce flushes,
  // input and URL agree again.
  useEffect(() => {
    const fromUrl = urlSearch.search ?? '';
    if (fromUrl !== search && fromUrl !== debouncedSearch) {
      setSearch(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
    // only depends on `urlSearch.search`; `search` / `debouncedSearch` are
    // read but should not retrigger the sync (otherwise typing fights it).
  }, [urlSearch.search]);

  // Mirror the search into the URL via `replace` so the detail page's
  // narrow rail matches and bookmarks / back-forward navigation work.
  // Only writes when the URL is stale to avoid render loops.
  useEffect(() => {
    const desired = debouncedSearch || undefined;
    if ((urlSearch.search ?? undefined) === desired) return;
    navigate({
      to: '/kontakty',
      search: { ...urlSearch, search: desired },
      replace: true,
    });
  }, [debouncedSearch, urlSearch, navigate]);

  const effectiveSellerId = sellerId ?? (showMine && myId ? myId : null);

  const filters = useMemo(
    () => ({
      type,
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
      ...(brandId ? { brand: String(brandId) } : {}),
      ...(effectiveSellerId ? { seller: String(effectiveSellerId) } : {}),
      ...(signedOnly && type !== 'rozjednane' ? { podpis_objednavky: 'Ano' } : {}),
    }),
    [type, debouncedSearch, brandId, effectiveSellerId, signedOnly],
  );

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useContactsInfiniteQuery(filters, {
    // Hold only while `/me` is in-flight to avoid an unfiltered fetch we'd
    // immediately replace. A `/me` failure must NOT lock the user out — we
    // fall back to "all sellers" until they explicitly pick one.
    enabled: !showMine || sellerId != null || !me.isLoading,
  });
  const brandsQuery = useContactBrandsQuery();
  const sellersQuery = useContactSellersQuery();
  const contacts = useMemo(
    () => data?.pages.flatMap((p) => p.contacts) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;

  const selected =
    contacts.find((c) => c.id_kontaktu === selectedId) ?? contacts[0] ?? null;

  const counts = useMemo(() => {
    const acc = { total: 0, withPlan: 0, signed: 0, withLms: 0 };
    for (const c of contacts) {
      acc.total += 1;
      if (c.plan_dates) acc.withPlan += 1;
      if (c.podpis_objednavky === 'Ano') acc.signed += 1;
      if ((c.lms_id ?? 0) > 0) acc.withLms += 1;
    }
    return acc;
  }, [contacts]);

  const brandLabel = useMemo(() => {
    if (brandId == null) return null;
    return brandsQuery.data?.brands.find((b) => b.id === brandId)?.nazev ?? null;
  }, [brandId, brandsQuery.data]);

  const sellerLabel = useMemo(() => {
    if (sellerId == null) return null;
    const s = sellersQuery.data?.sellers.find((x) => x.id === sellerId);
    if (!s) return null;
    return [s.jmeno, s.prijmeni].filter(Boolean).join(' ');
  }, [sellerId, sellersQuery.data]);

  return (
    <Shell
      rail={<AppRail active="kontakty" />}
      inspector={
        <KontaktInspector
          contact={selected}
          listSearch={urlSearch}
          onNaplanovat={(c) => setNaplanovatTarget({ contact: c })}
          onEditPlan={(c, plan) => setNaplanovatTarget({ contact: c, plan })}
          onPridatZpravu={(c) => setZpravaTarget(c)}
        />
      }
    >
      <Crown>
        <Breadcrumb segments={[{ label: 'Kontakty' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>Kontakty</DisplayHeading>
          <CrownMeta
            items={[
              { label: 'Celkem', value: formatInt(counts.total) },
              { label: 'S plánem', value: formatInt(counts.withPlan) },
              ...(type !== 'rozjednane'
                ? [{ label: 'Podepsané', value: formatInt(counts.signed) }]
                : []),
              { label: 'V LMS', value: formatInt(counts.withLms) },
            ]}
          />
        </Crown.Row>
        <Crown.Row className="mt-3">
          <Subtabs<KontaktyTab>
            aria-label="Stav kontaktu"
            items={tabOptions.map((o) => ({
              value: o.value,
              label: o.label,
              ...(o.value === type ? { count: counts.total } : {}),
            }))}
            value={type}
            onChange={(next) => {
              if (next === 'rozjednane' || next === 'ukoncene' || next === 'vsechny') setType(next);
              else goToKontaktyTab(navigate, next);
            }}
          />
        </Crown.Row>
      </Crown>

      <Toolband>
        <SearchInput
          placeholder="Jméno klienta nebo společnost…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          wrapperClassName="max-w-[360px] flex-1"
        />
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={brandId != null}>
              {brandLabel ?? 'Značka'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr značky</Menu.Label>
            <Menu.Item onSelect={() => setBrandId(null)}>Všechny značky</Menu.Item>
            <Menu.Separator />
            {(brandsQuery.data?.brands ?? []).map((b) => (
              <Menu.CheckItem
                key={b.id}
                checked={brandId === b.id}
                onCheckedChange={(checked) => setBrandId(checked ? b.id : null)}
              >
                {b.nazev}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="users" size={12} />} active={sellerId != null}>
              {sellerLabel ?? 'Prodejce'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr prodejce</Menu.Label>
            <Menu.Item onSelect={() => setSellerId(null)}>Všichni prodejci</Menu.Item>
            <Menu.Separator />
            {(sellersQuery.data?.sellers ?? []).map((s) => (
              <Menu.CheckItem
                key={s.id}
                checked={sellerId === s.id}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSellerId(s.id);
                    setShowMine(false);
                  } else {
                    setSellerId(null);
                  }
                }}
              >
                {[s.jmeno, s.prijmeni].filter(Boolean).join(' ')}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        {myId != null && sellerId == null && (
          <Chip
            active={showMine}
            onClick={() => setShowMine((v) => !v)}
          >
            Moje
          </Chip>
        )}
        {type !== 'rozjednane' && (
          <Chip
            active={signedOnly}
            onClick={() => setSignedOnly((v) => !v)}
          >
            Podepsané
          </Chip>
        )}
        <Toolband.Spacer />
        <Popover open={exportOpen} onOpenChange={setExportOpen}>
          <Popover.Trigger asChild>
            <Button
              size="sm"
              variant="secondary"
              icon={<Icon name="download" size={12} />}
              disabled={exporting}
              data-testid="kontakty-export"
            >
              {exporting ? 'Stahuji…' : 'Export'}
            </Button>
          </Popover.Trigger>
          <Popover.Content align="end" minWidth={300}>
            <Popover.Header
              eyebrow="Export kontaktů"
              hint={
                type === 'ukoncene' ? 'Ukončené' : type === 'vsechny' ? 'Všechny' : 'Rozjednané'
              }
            />
            <Popover.Body className="flex flex-col gap-3">
              <Fields cols={2}>
                <Field label="Poslední kontakt od">
                  <TextInput
                    type="date"
                    value={exportStart}
                    max={exportEnd || undefined}
                    onChange={(e) => setExportStart(e.target.value)}
                  />
                </Field>
                <Field label="Do">
                  <TextInput
                    type="date"
                    value={exportEnd}
                    min={exportStart || undefined}
                    onChange={(e) => setExportEnd(e.target.value)}
                  />
                </Field>
              </Fields>
              <Button
                size="sm"
                variant="primary"
                icon={<Icon name="download" size={12} />}
                disabled={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    await downloadContactsCsv({
                      type: type === 'ukoncene' ? '2' : type === 'vsechny' ? '3' : '1',
                      ...(brandId ? { brand: String(brandId) } : {}),
                      ...(effectiveSellerId ? { seller: String(effectiveSellerId) } : {}),
                      ...(exportStart ? { start: exportStart } : {}),
                      ...(exportEnd ? { end: exportEnd } : {}),
                    });
                    setExportOpen(false);
                  } catch (err) {
                    const message = err instanceof Error ? err.message : 'Export se nepodařil';
                    toast.error(message);
                  } finally {
                    setExporting(false);
                  }
                }}
                data-testid="kontakty-export-confirm"
              >
                {exporting ? 'Stahuji…' : 'Exportovat'}
              </Button>
            </Popover.Body>
          </Popover.Content>
        </Popover>
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="plus" size={12} />}
            onClick={() => navigate({ to: '/kontakty/novy' })}
          >
            Nový kontakt
          </Button>
        )}
      </Toolband>

      <div
        data-testid="kontakty-page"
        className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3 max-sm:px-0 max-sm:pb-4 max-sm:pt-1"
      >
        <DataTable<Contact>
          aria-label="Seznam kontaktů"
          variant="bordered"
          columns={contactColumns(type)}
          data={isLoading ? [] : contacts}
          getRowId={(c) => String(c.id_kontaktu)}
          getRowTint={(c) => (c.podpis_objednavky === 'Ano' ? 'mint' : undefined)}
          selectedId={selected ? String(selected.id_kontaktu) : undefined}
          onRowClick={(c) =>
            // Phone (14.3): tap navigates to the detail route (no inspector).
            isPhone
              ? navigate({
                  to: '/kontakty/$id',
                  params: { id: String(c.id_kontaktu) },
                  search: urlSearch,
                })
              : setSelectedId(c.id_kontaktu)
          }
          empty={
            isLoading ? (
              <ListSkeleton />
            ) : isError ? (
              <EmptyState
                eyebrow="Chyba"
                title="Kontakty se nepodařilo načíst"
                description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
              />
            ) : (
              <EmptyState
                eyebrow="Prázdné"
                title="Žádné kontakty"
                description="Zkuste rozšířit hledání nebo přidejte nový kontakt."
              />
            )
          }
          className="min-h-0 flex-1"
        />
      </div>
      <LoadMoreBar
        loaded={contacts.length}
        total={total}
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        unit="kontaktů"
      />
      {naplanovatTarget && (
        <NaplanovatModal
          open
          onOpenChange={(next) => {
            if (!next) setNaplanovatTarget(null);
          }}
          contactId={naplanovatTarget.contact.id_kontaktu}
          contactLabel={contactDisplayName(naplanovatTarget.contact)}
          plan={naplanovatTarget.plan}
        />
      )}
      {zpravaTarget && (
        <OdeslatZpravuModal
          open
          onOpenChange={(next) => {
            if (!next) setZpravaTarget(null);
          }}
          contactId={zpravaTarget.id_kontaktu}
          contactLabel={contactDisplayName(zpravaTarget)}
          defaultStatus={zpravaTarget.status ?? ''}
        />
      )}
    </Shell>
  );
}

function contactColumns(type: ContactType): DataTableColumn<Contact>[] {
  // Header reflects the cohort; per-row open/closed state is read from
  // `typ_kontaktu` so the "Všechny" view distinguishes each contact.
  const headerFinished = type === 'ukoncene';
  const mixed = type === 'vsechny';
  const widths = COLUMNS_TEMPLATE.split(' ');
  const cols: DataTableColumn<Contact>[] = [
    {
      id: 'klient',
      priority: 'primary',
      header: 'Klient',
      width: widths[0],
      cell: (c) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold tracking-[-0.01em] text-ink">
            {contactDisplayName(c)}
          </span>
          {c.spolecnost && c.jmeno_klienta && (
            <span className="truncate text-[11px] text-ink-3">{c.spolecnost}</span>
          )}
        </div>
      ),
    },
    {
      id: 'vozidlo',
      priority: 'secondary',
      header: 'Vozidlo',
      width: widths[1],
      cell: (c) => {
        const brand = vehicleBrandLabel(c);
        const model = vehicleModelLabel(c);
        const label = [brand, model].filter(Boolean).join(' ').trim();
        return (
          <span className="truncate text-[12px] text-ink-2">{label || '—'}</span>
        );
      },
    },
    {
      id: 'status',
      priority: 'secondary',
      header: 'Status',
      width: widths[2],
      cell: (c) => (
        <span className="whitespace-normal break-words text-[11.5px] leading-snug text-ink-2">
          {c.status || '—'}
        </span>
      ),
    },
    {
      id: 'days',
      priority: 'meta',
      header: headerFinished ? 'Dnů od ukončení' : 'Dnů od kontaktu',
      width: widths[3],
      align: 'right',
      cell: (c) => {
        const ref = headerFinished ? c.datum_ukonceni : c.posledni_kontakt;
        const d = daysSince(ref);
        if (d == null) return <span className="font-mono text-[11px] text-ink-3">—</span>;
        const tone = d > 30 ? 'text-warn' : 'text-ink-2';
        return <span className={`font-mono text-[11.5px] ${tone}`}>{d}</span>;
      },
    },
    {
      id: 'prodejce',
      header: 'Prodejce',
      width: widths[4],
      cell: (c) => {
        const dot = userDotColor(c.prodejce_barva);
        return (
          <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-ink-3">
            {dot && (
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: dot }}
              />
            )}
            <span className="truncate">{c.prodejce_jmeno || '—'}</span>
          </span>
        );
      },
    },
    {
      id: 'flags',
      priority: 'meta',
      header: '',
      width: widths[5],
      align: 'right',
      cell: (c) => {
        const rowFinished = c.typ_kontaktu === 2;
        return (
          <div className="flex flex-wrap items-center justify-end gap-x-1.5 gap-y-1">
            {mixed && rowFinished && <Badge size="sm" tone="neutral">Ukončený</Badge>}
            {c.podpis_objednavky === 'Ano' && <Badge size="sm" tone="ok">Podpis</Badge>}
            {(c.lms_id ?? 0) > 0 && <Badge size="sm" tone="accent">LMS</Badge>}
            {!rowFinished && c.plan_dates && <Badge size="sm" tone="warn">Plán</Badge>}
          </div>
        );
      },
    },
  ];
  return cols;
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-px p-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function KontaktInspector({
  contact,
  listSearch,
  onNaplanovat,
  onEditPlan,
  onPridatZpravu,
}: {
  contact: Contact | null;
  /** Current list URL filters — forwarded to the detail link so its master
   *  rail mirrors the cohort the user was browsing. */
  listSearch: KontaktySearch;
  onNaplanovat: (contact: Contact) => void;
  onEditPlan: (contact: Contact, plan: ContactPlan) => void;
  onPridatZpravu: (contact: Contact) => void;
}) {
  const navigate = useNavigate();
  const { can } = useModuleAccess();
  const canWrite = can('kontakty', 'write');
  // Fetch full detail only when the inspector has a contact selected and the
  // legacy `plan_dates` summary tells us there *is* an active plan worth
  // surfacing as a clickable chip. The query is the same one the detail page
  // uses, so the cache is shared and warmed for free. Fetched for every
  // selected contact so the inspector can preview the latest messages (and the
  // active-plan chips for open contacts).
  const detailQuery = useContactDetailQuery(contact ? contact.id_kontaktu : null);
  const activePlans = useMemo<ContactPlan[]>(() => {
    if (!detailQuery.data) return [];
    return detailQuery.data.contact.plany.filter((p) => p.stav === 'aktivni');
  }, [detailQuery.data]);
  // Newest-first; show the 5 most recent as a preview.
  const recentMessages = useMemo(
    () => detailQuery.data?.contact.zpravy.slice(0, 5) ?? [],
    [detailQuery.data],
  );
  if (!contact) {
    return (
      <Inspector>
        <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Vyberte kontakt
        </div>
      </Inspector>
    );
  }

  const isFinished = contact.typ_kontaktu === 2;
  const brand = vehicleBrandLabel(contact);
  const model = vehicleModelLabel(contact);
  const vehicle = [brand, model].filter(Boolean).join(' ').trim();

  return (
    <Inspector className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — <b>№ {contact.id_kontaktu}</b>
            {(contact.lms_id ?? 0) > 0 && <> · LMS</>}
          </>
        }
        eyebrowEnd={
          <Marker
            tone={
              // Open → neutral (still in progress). Closed → green when it ended
              // with a signed order, red when it closed without one.
              !isFinished
                ? 'neutral'
                : contact.podpis_objednavky === 'Ano'
                  ? 'ok'
                  : 'danger'
            }
          >
            {isFinished ? 'Ukončený' : 'Rozjednaný'}
          </Marker>
        }
        title={contactDisplayName(contact)}
        sub={
          contact.spolecnost && contact.jmeno_klienta ? (
            <span className="inline-flex items-center gap-2">
              <Pill>Společnost</Pill>
              <span className="font-sans text-[12.5px] text-ink-2">{contact.spolecnost}</span>
            </span>
          ) : null
        }
      />
      <Inspector.Body>
        <Inspector.Section heading="— Vozidlo">
          <FieldRow k="Značka" v={brand || '—'} />
          <FieldRow k="Model" v={model || '—'} />
          {contact.nazev_vozu && contact.nazev_vozu !== model && (
            <FieldRow k="LMS vůz" v={contact.nazev_vozu} />
          )}
        </Inspector.Section>
        <Inspector.Section heading="— Stav">
          <FieldRow k="Status" v={contact.status || '—'} />
          <FieldRow
            k="První návštěva"
            v={contact.prvni_navsteva ? formatDate(contact.prvni_navsteva) : '—'}
            mono
          />
          <FieldRow
            k="Poslední kontakt"
            v={contact.posledni_kontakt ? formatDate(contact.posledni_kontakt) : '—'}
            mono
          />
          {!isFinished && contact.plan_dates && (
            <FieldRow
              k="Plánováno"
              mono
              v={
                canWrite && activePlans.length > 0 ? (
                  <span className="inline-flex flex-wrap gap-1">
                    {activePlans.map((p) => (
                      <button
                        key={p.id_planu}
                        type="button"
                        onClick={() => onEditPlan(contact, p)}
                        title={p.popis || 'Upravit plán'}
                        className="border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] tracking-[-0.02em] text-ink-2 hover:border-accent hover:text-accent-text focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                      >
                        {formatDate(p.datum_planu)}
                        {p.cas_planu ? ` ${p.cas_planu}` : ''}
                      </button>
                    ))}
                  </span>
                ) : (
                  contact.plan_dates
                )
              }
            />
          )}
          {isFinished && contact.datum_ukonceni && (
            <FieldRow k="Ukončen" v={formatDate(contact.datum_ukonceni)} mono />
          )}
          <FieldRow
            k="Podpis objednávky"
            v={contact.podpis_objednavky === 'Ano' ? 'Ano' : 'Ne'}
          />
          <FieldRow k="Prodejce" v={contact.prodejce_jmeno || '—'} />
        </Inspector.Section>
        <Inspector.Section heading="— Kontakt">
          <FieldRow k="Telefon" v={contact.telefon || '—'} mono />
          <FieldRow k="E-mail" v={contact.email || '—'} />
          <FieldRow k="Zástupce" v={contact.zastupce || '—'} />
        </Inspector.Section>
        {vehicle && isFinished && contact.vysledek && (
          <Inspector.Section heading="— Výsledek">
            <p className="whitespace-pre-line text-[12.5px] text-ink-2">
              {linkify(contact.vysledek)}
            </p>
          </Inspector.Section>
        )}
        {contact.poznamka && contact.poznamka.trim() && (
          <Inspector.Section heading="— Poznámka">
            <p className="whitespace-pre-line text-[12.5px] text-ink-2">
              {linkify(contact.poznamka)}
            </p>
          </Inspector.Section>
        )}
        {recentMessages.length > 0 && (
          <Inspector.Section heading="— Zprávy">
            <div className="flex flex-col gap-3">
              {recentMessages.map((m) => (
                <div key={m.id} className="border-l border-line pl-2.5">
                  <div className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">
                    <span className="truncate">{m.prodejce_jmeno || '—'}</span>
                    <span className="shrink-0">{formatDate(m.datum_kontaktu)}</span>
                  </div>
                  <div className="mt-1 max-h-[4.6em] overflow-hidden">
                    <MessageBody zprava={m.zprava} />
                  </div>
                </div>
              ))}
            </div>
          </Inspector.Section>
        )}
      </Inspector.Body>
      <Inspector.Foot>
        {!isFinished && canWrite && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="calendar" size={12} />}
            onClick={() => onNaplanovat(contact)}
          >
            Naplánovat
          </Button>
        )}
        {canWrite && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="plus" size={12} />}
            onClick={() => onPridatZpravu(contact)}
          >
            Zpráva
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() =>
            navigate({
              to: '/kontakty/$id',
              params: { id: String(contact.id_kontaktu) },
              search: listSearch,
            })
          }
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}

